import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { Cancel, Language } from '@mui/icons-material'
import DateTimePicker from 'components/form/DateTimePicker'
import InputField from 'components/form/InputField'
import SearchButton from 'components/form/SearchButton'
import SubmitButton from 'components/form/SubmitButton'

import { selectPrivileges } from 'store/privileges/slice'
import { getPrivileges } from 'store/privileges/thunks'
import { clearPrivilege, selectUser } from 'store/user/slice'
import { addNewPrivilege, updatePrivilege, cancelPrivilege } from 'store/user/thunks'
import { formatDate } from 'utils/formatDate'
import PrivilegeSelect from './PrivilegeSelect'
import { selectCountries } from 'store/countries/slice'
import { selectLocale } from 'store/locale/slice'
import { FormattedMessageWrapper } from 'components/ui/FormattedMessageWrapper'

interface Props {
  open: boolean
  toggle: () => void
  user: User
  privilege: UserPrivilege | null
  isActive: (privilege: UserPrivilege) => boolean
}

export default function UserPrivilegeModal({ open, toggle, user, privilege, isActive }: Props): JSX.Element {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { isSubmitSuccessful },
  } = useForm<PrivilegePayload>({
    defaultValues: { typeId: null, validFrom: '', validTo: '', notes: '', targetCountries: [], targetLanguages: [] },
  })
  const dispatch = useDispatch()
  const intl = useIntl()
  const { privilegeStatus, cancelPrivilegeStatus } = useSelector(selectUser)
  const { privileges } = useSelector(selectPrivileges)
  const { countries } = useSelector(selectCountries)
  const { availableLanguages } = useSelector(selectLocale)

  const handleClose = () => {
    reset({
      typeId: privilege?.typeId,
      validFrom: '',
      validTo: '',
      notes: '',
      targetCountries: privilege?.targetCountries,
      targetLanguages: privilege?.targetLanguages,
    })
    toggle()
  }

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset()
    }
  }, [isSubmitSuccessful])

  useEffect(() => {
    if (privilege) {
      reset({
        typeId: privilege.typeId,
        validFrom: privilege.validFrom ? formatDate(new Date(privilege.validFrom.date)) : '',
        validTo: privilege.validTo ? formatDate(new Date(privilege.validTo.date)) : '',
        notes: privilege.notes,
        targetCountries: privilege.targetCountries,
        targetLanguages: privilege.targetLanguages,
      })
    } else {
      reset({
        typeId: null,
        validFrom: '',
        validTo: '',
        notes: '',
        targetCountries: [],
        targetLanguages: [],
      })
    }
  }, [reset, privilege])

  useEffect(() => {
    if (!privilege) {
      dispatch(clearPrivilege())
    }
  }, [dispatch, privilege])

  useEffect(() => {
    if (privileges.length === 0) {
      dispatch(getPrivileges())
    }
  }, [dispatch, privileges])

  useEffect(() => {
    if ([privilegeStatus, cancelPrivilegeStatus].includes('succeeded')) {
      dispatch(clearPrivilege())
      toggle()
    }
  }, [dispatch, privilegeStatus, cancelPrivilegeStatus, toggle])

  const getPrivilegeOptions = () => {
    const filteredPrivileges = privileges.filter((privilege) => privilege.name !== 'GOLD_MEMBERSHIP')
    const privilegeOptions = filteredPrivileges.map((privilege: Privilege) => {
      return { key: privilege.id, label: privilege.name, metadata: privilege?.metadata }
    })
    return privilegeOptions
  }

  const submitForm = (data: PrivilegePayload) => {
    if (privilege) {
      updateUserPrivilege(data)
    } else {
      addUserPrivilege(data)
    }
  }

  const getValidFromDate = (date?: Date) => {
    const validFromDefault = date ? new Date(date) : new Date()
    if (!date) {
      return new Date(validFromDefault)
    }

    const validFrom = validFromDefault.toDateString()
    const previousValidFrom = privilege?.validFrom ? new Date(privilege?.validFrom.date) : null

    return previousValidFrom?.toDateString() !== validFrom
      ? validFrom
      : `${validFrom} ${previousValidFrom.toLocaleTimeString()}`
  }

  const getValidToDate = (date?: Date) => {
    const validToDefault = date ? new Date(date) : new Date()
    if (!date) {
      validToDefault.setFullYear(validToDefault.getFullYear() + 10)
      return new Date(validToDefault)
    }

    const validTo = validToDefault.toDateString()
    const previousValidTo = privilege?.validTo ? new Date(privilege?.validTo.date) : null

    return previousValidTo?.toDateString() !== validTo
      ? `${validTo} 23:59:59`
      : `${validTo} ${previousValidTo.toLocaleTimeString()}`
  }

  const addUserPrivilege = (data: PrivilegePayload) => {
    const { typeId, validFrom, validTo, notes, targetCountries, targetLanguages } = data
    const hasCountryAndLanguageOptions = renderCountryAndLanguage(typeId)
    if (user.id && user.fullName) {
      const payload: UserPrivilegePayload = {
        id: user.id,
        successMessage: intl.formatMessage({ id: 'strings.privilegeAddedSuccessfully' }),
        data: {
          typeId: typeId,
          validFrom: validFrom ? new Date(validFrom) : getValidFromDate(),
          validTo: validTo ? getValidToDate(validTo) : getValidToDate(),
          notes,
          targetCountries: hasCountryAndLanguageOptions && isNotEmpty(targetCountries) ? targetCountries : undefined,
          targetLanguages: hasCountryAndLanguageOptions && isNotEmpty(targetLanguages) ? targetLanguages : undefined,
        },
      }
      dispatch(addNewPrivilege(payload))
    }
  }

  const isNotEmpty = (arr: (string | number)[] | undefined) => {
    if (arr?.length === 0) {
      return false
    }
    return true
  }

  const updateUserPrivilege = (data: PrivilegePayload) => {
    const { typeId, validFrom, validTo, notes, targetCountries, targetLanguages } = data
    const hasCountryAndLanguageOptions = renderCountryAndLanguage(typeId)
    if (privilege?.id && user?.id) {
      const payload: UserPrivilegePayload = {
        id: privilege.id,
        successMessage: intl.formatMessage({ id: 'strings.privilegeUpdatedSuccessfully' }),
        data: {
          userId: user.id,
          typeId: typeId,
          validFrom: validFrom ? getValidFromDate(validFrom) : getValidFromDate(),
          validTo: validTo ? getValidToDate(validTo) : getValidToDate(),
          notes,
          targetCountries: hasCountryAndLanguageOptions && isNotEmpty(targetCountries) ? targetCountries : undefined,
          targetLanguages: hasCountryAndLanguageOptions && isNotEmpty(targetLanguages) ? targetLanguages : undefined,
        },
      }
      dispatch(updatePrivilege(payload))
    }
  }

  const cancelUserPrivilege = () => {
    if (privilege?.id && user?.id) {
      const payload: UserPrivilegePayload = {
        id: privilege.id,
        successMessage: intl.formatMessage({ id: 'strings.privilegeCancelledSuccessfully' }),
        data: {
          userId: user.id,
          typeId: privilege.typeId,
          validFrom: privilege.validFrom.date,
          validTo: new Date(),
          notes: privilege.notes,
          targetCountries: privilege.targetCountries,
          targetLanguages: privilege.targetLanguages,
        },
      }
      dispatch(cancelPrivilege(payload))
    }
  }

  const getValidFromTooltipContent = () => {
    return (
      <>
        <div>{intl.formatMessage({ id: 'strings.validFromTooltipContent1' })}</div>
        <div>{intl.formatMessage({ id: 'strings.validFromTooltipContent2' })}</div>
      </>
    )
  }

  const getValidToTooltipContent = () => {
    return (
      <>
        <div>{intl.formatMessage({ id: 'strings.validToTooltipContent1' })}</div>
        <div>{intl.formatMessage({ id: 'strings.validToTooltipContent2' })}</div>
      </>
    )
  }

  const getCountryOptions = (): SelectOption[] => {
    const fielteredCountries = countries.filter((countries) => countries.name)
    const countriesOptions = fielteredCountries.map((country: Country) => {
      return { key: country.id, label: country.name }
    })

    return countriesOptions
  }
  const getLanguageOptions = (): SelectOption[] => {
    const languages = availableLanguages.filter((availableLanguages) => availableLanguages.code)
    const displayNames = new Intl.DisplayNames(['en'], { type: 'language' })
    const languageOptions = languages.map((languages: Language) => {
      return { key: languages.code, label: displayNames.of(languages.code) || '' }
    })

    return languageOptions
  }

  const renderCountryAndLanguage = (privilegeId: number | null) => {
    const privilegeOptions = getPrivilegeOptions()
    const privilegeMetadata = privilegeOptions.find((option) => option.key === privilegeId)?.metadata
    if (privilegeMetadata && privilegeMetadata.showCountryOptions && privilegeMetadata.showLanguageOptions) {
      return true
    }
    return false
  }

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <form>
        <DialogTitle id="form-dialog-title">
          {privilege
            ? intl.formatMessage({ id: 'strings.updatePrivilege' })
            : intl.formatMessage({ id: 'strings.addNewPrivilege' })}
          <CloseModal onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
          <>
            <StyledPrivilegeSelect
              name="typeId"
              label={intl.formatMessage({ id: 'strings.privilege' })}
              control={control}
              options={getPrivilegeOptions()}
              required
            />

            {renderCountryAndLanguage(watch('typeId')) && (
              <>
                <CountrySelect
                  name="targetCountries"
                  label={intl.formatMessage({ id: 'strings.countries' })}
                  control={control}
                  options={getCountryOptions()}
                  multiple
                  tooltipContent={intl.formatMessage({ id: 'strings.countriesInfo' })}
                  syncArrayChange={(value) => {
                    if (Array.isArray(value) && value.every((v) => typeof v === 'number')) {
                      setValue('targetCountries', value)
                    }
                  }}
                />

                <LanguageSelect
                  name="targetLanguages"
                  label={intl.formatMessage({ id: 'strings.language' })}
                  control={control}
                  options={getLanguageOptions()}
                  multiple
                  tooltipContent={<FormattedMessageWrapper id={'strings.languageInfo'} />}
                  syncArrayChange={(value) => {
                    if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
                      setValue('targetLanguages', value)
                    }
                  }}
                />
              </>
            )}

            <DateTimePicker
              register={register}
              name="validFrom"
              label={intl.formatMessage({ id: 'strings.validFrom' })}
              dateOnly
              tooltipContent={getValidFromTooltipContent()}
            />
            <DateTimePicker
              register={register}
              name="validTo"
              label={intl.formatMessage({ id: 'strings.validTo' })}
              dateOnly
              tooltipContent={getValidToTooltipContent()}
            />
            <InputField
              register={register}
              name="notes"
              type="text"
              label={intl.formatMessage({ id: 'strings.notes' })}
              outlined
            />
          </>
        </DialogContent>
        <DialogActions>
          {privilege && (
            <CancelPrivilege
              disabled={!isActive(privilege)}
              status={cancelPrivilegeStatus}
              text={intl.formatMessage({ id: 'strings.cancelPrivilege' })}
              onClick={cancelUserPrivilege}
            />
          )}
          <Button onClick={handleClose} color="primary">
            <FormattedMessage id={'strings.cancel'} />
          </Button>
          <SubmitButton
            handleSubmit={handleSubmit(submitForm)}
            status={privilegeStatus}
            text={<FormattedMessage id={'forms.save'} />}
          />
        </DialogActions>
      </form>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    min-width: 400px;
  }

  & .MuiTextField-root {
    margin: 20px 20px 20px 0;
  }

  & .MuiAlert-root {
    margin-bottom: 20px;
  }
`

const CloseModal = styled(Cancel)`
  float: right;
  cursor: pointer;
`

const CancelPrivilege = styled(SearchButton)`
  background-color: ${(props) => props.theme.customPalette.error};
  color: ${(props) => props.theme.customPalette.light};

  &:hover {
    background-color: ${(props) => props.theme.customPalette.error};
    opacity: 0.85;
  }
`

const StyledPrivilegeSelect = styled(PrivilegeSelect)`
  margin: 20px 0;
`

const CountrySelect = styled(PrivilegeSelect)`
  margin: 20px 0;
`

const LanguageSelect = styled(PrivilegeSelect)`
  margin: 20px 0;
`
